import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { CategoryIcon } from "../CategoryIcon";
import { DAY_ONE_UNLOCKS, LANDING_TRACKS } from "../../data/landingContent";
import { VAULT } from "../../data/vaultStats";

const GRID_SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

export const LandingTrackPreview = ({ lifetimeHref }) => (
  <section className="border-b border-white/10 bg-[#050407]" data-testid="landing-track-preview">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:px-10 lg:py-28">
      <div className="max-w-4xl">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-purple-300">A look inside · 4 of {VAULT.tracks} tracks</div>
        <h2 className="mt-4 font-display text-3xl font-black leading-[1.05] text-white sm:text-4xl lg:text-5xl">Choose a commercial outcome. Follow the prompts that build it.</h2>
        <p className="mt-5 max-w-3xl text-base leading-relaxed text-zinc-400 sm:text-lg">Every track is sequenced around something you can publish, sell or deliver—not a broad topic to study.</p>
      </div>

      <div className="mt-10 grid gap-3 lg:grid-cols-12">
        {LANDING_TRACKS.map((track, index) => (
          <Link key={track.slug} to={`/p/${track.slug}`} className={`group min-h-[17rem] border border-zinc-800 bg-[#0a090c] p-6 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-1 hover:border-purple-400/60 hover:bg-[#0d0a11] ${GRID_SPANS[index]}`} data-testid={`preview-card-${track.slug}`}>
            <div className="flex items-start justify-between gap-4">
              <CategoryIcon image={track.image_icon} size={64} className="h-16 w-16" loading="lazy" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600">Track {String(track.order).padStart(2, "0")}</span>
            </div>
            <h3 className="mt-8 font-display text-2xl font-bold text-white sm:text-3xl">{track.name}</h3>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">{track.tagline}</p>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-4 text-xs">
              <span className="text-emerald-300">Outcome · {track.outcome}</span>
              <span className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-purple-300 transition-transform group-hover:translate-x-1">Preview {track.prompt_count} prompts <ArrowRight size={13} /></span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-3 grid border border-zinc-800 bg-[#09080b] lg:grid-cols-12" data-testid="landing-day1">
        <div className="border-b border-zinc-800 p-6 md:p-8 lg:col-span-5 lg:border-b-0 lg:border-r">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">What unlocks on day one</div>
          <h3 className="mt-3 font-display text-3xl font-black text-white">The complete working system.</h3>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">Not just a larger prompt folder. The vault adds navigation, in-app AI runs, saved progress and a clear next action.</p>
          <Link to={lifetimeHref} className="mt-7 inline-flex min-h-12 items-center gap-3 bg-amber-300 px-5 text-xs font-black uppercase tracking-[0.15em] text-black transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-amber-200" data-testid="preview-cta-unlock">
            Unlock all {VAULT.tracks} tracks · £25 <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid gap-px bg-zinc-800 sm:grid-cols-2 lg:col-span-7">
          {DAY_ONE_UNLOCKS.map(([title, detail], index) => (
            <div key={title} className="bg-[#09080b] p-5 md:p-6" data-testid={`day1-row-${index}`}>
              <Check size={16} className="text-emerald-300" />
              <div className="mt-3 font-semibold text-white">{title}</div>
              <div className="mt-1 text-sm leading-relaxed text-zinc-500">{detail}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);